/** Project link helpers — preview, PDF icons, clickable URLs. */

/** Icon size on PDF (mm), aligned with ~10px preview icon */
export const PDF_PROJECT_LINK_ICON_MM = 3.8;

const ICON_PX = 28;

const PROJECT_LINK_SVG = {
    github: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512"><path fill="#222222" d="M165.9 397.4c0 2-2.3 3.6-5.2 3.6-3.3.3-5.6-1.8-5.6-3.6 0-2 2.3-3.6 5.2-3.6 3-.3 5.6 1.8 5.6 3.6zm-31.1-4.5c-.7 2 1.3 4.3 4.3 4.9 2.6 1 5.6 0 6.2-2s-1.3-4.3-4.3-5.2c-2.6-.7-5.5.3-6.2 2.3zm44.2-1.7c-2.9.7-4.9 2.6-4.6 4.9.3 2 2.9 3.3 5.9 2.6 2.9-.7 4.9-2.6 4.6-4.6-.3-1.9-3-3.2-5.9-2.9zM244.8 8C106.1 8 0 113.3 0 252c0 110.9 69.8 205.8 169.5 239.2 12.8 2.3 17.3-5.6 17.3-12.1 0-6.2-.3-40.4-.3-61.4 0 0-70 15-84.7-29.8 0 0-11.4-29.1-27.8-36.6 0 0-22.9-15.7 1.6-15.4 0 0 24.9 2 38.6 25.8 21.9 38.6 58.6 27.5 72.9 20.9 2.3-16 8.8-27.1 16-33.7-55.9-6.2-112.3-14.3-112.3-110.5 0-27.5 7.6-41.3 23.6-58.9-2.6-6.5-11.1-33.3 2.6-67.9 20.9-6.5 69 27 69 27 20-5.6 41.5-8.5 62.8-8.5s42.8 2.9 62.8 8.5c0 0 48.1-33.6 69-27 13.7 34.7 5.2 61.4 2.6 67.9 16 17.7 25.8 31.5 25.8 58.9 0 96.5-58.9 104.2-114.8 110.5 9.2 7.9 17 22.9 17 46.4 0 33.7-.3 75.4-.3 83.6 0 6.5 4.6 14.4 17.3 12.1C428.2 457.8 496 362.9 496 252 496 113.3 383.5 8 244.8 8z"/></svg>`,
    link: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="#222222" d="M326.612 185.391c9.757 9.757 9.757 25.588 0 35.345l-84.305 84.305c-9.757 9.757-25.588 9.757-35.345 0-9.757-9.757-9.757-25.588 0-35.345l84.305-84.305c9.757-9.757 25.588-9.757 35.345 0zm-145.373 23.373c9.757-9.757 25.588-9.757 35.345 0l84.305 84.305c9.757 9.757 9.757 25.588 0 35.345-9.757 9.757-25.588 9.757-35.345 0l-84.305-84.305c-9.757-9.757-9.757-25.588 0-35.345zM256 8C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8 256 8zm0 448c-110.28 0-200-89.72-200-200S145.72 56 256 56s200 89.72 200 200-89.72 200-200 200z"/></svg>`,
};

const PNG_CACHE = new Map();

/**
 * @param {string} link
 * @returns {string}
 */
export function normalizeProjectUrl(link) {
    const raw = String(link ?? "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    return `https://${raw}`;
}

/**
 * @param {string} link
 * @returns {{ label: string, isGithub: boolean, iconKey: "github" | "link" }}
 */
export function getProjectLinkMeta(link) {
    const href = normalizeProjectUrl(link);
    if (!href) return { label: "", isGithub: false, iconKey: "link" };
    const lower = href.toLowerCase();
    if (lower.includes("github.com")) {
        return { label: "GitHub", isGithub: true, iconKey: "github" };
    }
    if (lower.includes("gitlab.com")) {
        return { label: "GitLab", isGithub: false, iconKey: "link" };
    }
    return { label: "Link", isGithub: false, iconKey: "link" };
}

/**
 * @param {string} svgMarkup
 * @param {number} pixelSize
 */
function rasterizeSvgToPng(svgMarkup, pixelSize = ICON_PX) {
    const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
    const cacheKey = `${svgDataUrl}|${pixelSize}`;
    if (PNG_CACHE.has(cacheKey)) {
        return Promise.resolve(PNG_CACHE.get(cacheKey));
    }

    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = pixelSize;
                canvas.height = pixelSize;
                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    reject(new Error("Canvas unsupported"));
                    return;
                }
                ctx.clearRect(0, 0, pixelSize, pixelSize);
                ctx.drawImage(img, 0, 0, pixelSize, pixelSize);
                const png = canvas.toDataURL("image/png");
                PNG_CACHE.set(cacheKey, png);
                resolve(png);
            } catch (err) {
                reject(err);
            }
        };
        img.onerror = () => reject(new Error("Project link icon rasterize failed"));
        img.src = svgDataUrl;
    });
}

/** @returns {Promise<Record<string, string>>} */
export async function preloadProjectLinkIcons() {
    const entries = await Promise.all(
        Object.entries(PROJECT_LINK_SVG).map(async ([key, svg]) => [
            key,
            await rasterizeSvgToPng(svg),
        ])
    );
    return Object.fromEntries(entries);
}

/**
 * Width (mm) reserved beside project title for link icon/label.
 * @param {import("jspdf").jsPDF} pdf
 * @param {string} link
 * @param {Record<string, string> | null} icons
 */
export function measurePdfProjectLinkWidth(pdf, link, icons) {
    const meta = getProjectLinkMeta(link);
    if (!meta.label) return 0;
    if (meta.isGithub && icons?.github) {
        return PDF_PROJECT_LINK_ICON_MM + 1.5;
    }
    pdf.setFont("times", "normal");
    pdf.setFontSize(10);
    return pdf.getTextWidth(meta.label) + 1.5;
}

/**
 * @param {import("jspdf").jsPDF} pdf
 * @param {number} x
 * @param {number} y
 * @param {number} w
 * @param {number} h
 * @param {string} url
 */
function addPdfLinkArea(pdf, x, y, w, h, url) {
    const href = normalizeProjectUrl(url);
    if (!href || typeof pdf.link !== "function") return;
    pdf.link(x, y, w, h, { url: href });
}

/**
 * Draw GitHub icon or text link in PDF; returns width used (mm).
 * @param {import("jspdf").jsPDF} pdf
 * @param {number} x
 * @param {number} titleY — text baseline (mm)
 * @param {string} link
 * @param {string} url
 * @param {Record<string, string> | null} icons
 * @returns {number}
 */
export function writePdfProjectLink(pdf, x, titleY, link, url, icons) {
    const href = normalizeProjectUrl(url);
    const meta = getProjectLinkMeta(link);
    if (!href || !meta.label) return 0;

    pdf.setTextColor(0, 0, 0);

    if (meta.isGithub && icons?.github) {
        const size = PDF_PROJECT_LINK_ICON_MM;
        const topY = titleY - size * 0.82;
        pdf.addImage(icons.github, "PNG", x, topY, size, size, undefined, "FAST");
        addPdfLinkArea(pdf, x, topY, size, size, href);
        return size + 1.5;
    }

    pdf.setFont("times", "normal");
    pdf.setFontSize(10);
    const label = meta.label;

    if (typeof pdf.textWithLink === "function") {
        pdf.textWithLink(label, x, titleY, { url: href });
    } else {
        pdf.text(label, x, titleY);
        const w = pdf.getTextWidth(label);
        const h = (pdf.getFontSize() * 0.352778) / 2 + 2;
        addPdfLinkArea(pdf, x, titleY - h + 1, w, h, href);
    }

    return pdf.getTextWidth(label) + 1.5;
}

/** @param {string} href */
export function openProjectLink(href) {
    const url = normalizeProjectUrl(href);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
}
